let __assign =
    (this && this.__assign) ||
    function () {
        __assign =
            Object.assign ||
            function (t) {
                for (let s, i = 1, n = arguments.length; i < n; i++) {
                    s = arguments[i];
                    for (const p in s) {
                        if (Object.prototype.hasOwnProperty.call(s, p)) {
                            t[p] = s[p];
                        }
                    }
                }
                return t;
            };
        return __assign.apply(this, arguments);
    };
Object.defineProperty(exports, '__esModule', { value: true });
const queryString = require('query-string');
const reactAdmin = require('react-admin');
/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts
 * DELETE       => DELETE http://my.api.url/posts/123
 */
exports.default = (apiUrl, httpClient, getStore, setPaginationToken) => {
    if (httpClient === void 0) {
        httpClient = reactAdmin.fetchUtils.fetchJson;
    }
    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHTTP = (type, resource, params) => {
        let _a;
        let url = '';
        const options = {};
        switch (type) {
            case reactAdmin.GET_LIST: {
                const _b = params.pagination;
                const { page } = _b;
                const { perPage } = _b;
                const _c = params.sort;
                const { field } = _c;
                const { order } = _c;
                const store = getStore();
                const state = store.getState();
                const { custom } = state;
                const { pagination = {} } = custom;
                const paginationToken = pagination[resource]
                    ? pagination[resource][page]
                    : null;
                const query = {
                    sort: JSON.stringify([field, order]),
                    range: JSON.stringify([
                        (page - 1) * perPage,
                        page * perPage - 1,
                        ...(resource === 'ecommerce-special-initiatives'
                            ? []
                            : [paginationToken]),
                    ]),
                    pagination: JSON.stringify({
                        paginationToken,
                        limit: perPage,
                    }),
                    filter: JSON.stringify(params.filter),
                };
                url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;
                break;
            }
            case reactAdmin.GET_ONE:
                url = `${apiUrl}/${resource}/${encodeURIComponent(params.id)}`;
                break;
            case reactAdmin.GET_MANY: {
                const query = {
                    filter: JSON.stringify({ id: params.ids }),
                };
                url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;
                break;
            }
            case reactAdmin.GET_MANY_REFERENCE: {
                const _d = params.pagination;
                const { page } = _d;
                const { perPage } = _d;
                const _e = params.sort;
                const { field } = _e;
                const { order } = _e;
                const query = {
                    sort: JSON.stringify([field, order]),
                    range: JSON.stringify([
                        (page - 1) * perPage,
                        page * perPage - 1,
                    ]),
                    filter: JSON.stringify(
                        __assign(
                            {},
                            params.filter,
                            ((_a = {}), (_a[params.target] = params.id), _a)
                        )
                    ),
                };
                url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;
                break;
            }
            case reactAdmin.UPDATE:
                url = `${apiUrl}/${resource}/${encodeURIComponent(params.id)}`;
                options.method = 'PUT';
                options.body = JSON.stringify(params.data);
                break;
            case reactAdmin.CREATE:
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = JSON.stringify(params.data);
                break;
            case reactAdmin.DELETE:
                url = `${apiUrl}/${resource}/${encodeURIComponent(params.id)}`;
                options.method = 'DELETE';
                break;
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }
        return { url, options };
    };
    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} Data response
     */
    const convertHTTPResponse = (response, type, resource, params) => {
        const { headers } = response;
        const { json } = response;
        switch (type) {
            case reactAdmin.GET_LIST:
            case reactAdmin.GET_MANY_REFERENCE:
                // if (!headers.has('content-range')) {
                //   throw new Error('The Content-Range header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?');
                // }
                const paginationToken = headers.get('pagination-token');
                const { pagination } = params;
                const store = getStore();
                store.dispatch(
                    setPaginationToken({ resource, paginationToken })
                );
                console.log('[CUSTOM PROVIDER] => ', headers);
                console.log('[JSON] => ', json);
                const total = parseInt(
                    headers.get('content-range').split('/').pop(),
                    10
                );
                return {
                    data: json,
                    total:
                        resource !== 'ecommerce-special-initiatives' &&
                        total === 1
                            ? pagination.perPage * pagination.page
                            : total,
                };
            case reactAdmin.CREATE:
                return {
                    data:
                        resource === 'ecommerce-special-initiatives'
                            ? json
                            : __assign({}, params.data, {
                                  id: json.id,
                                  hasTextBeenSanitized:
                                      json?.hasTextBeenSanitized,
                              }),
                };
            case reactAdmin.DELETE_MANY: {
                return { data: json || [] };
            }
            default:
                return { data: json };
        }
    };
    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    const provider = (type, resource, params) => {
        // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
        if (type === reactAdmin.UPDATE_MANY) {
            return Promise.all(
                params.ids.map((id) =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'PUT',
                        body: JSON.stringify(params.data),
                    })
                )
            ).then((responses) => ({
                data: responses.map((response) => response.json),
            }));
        }
        // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
        if (type === reactAdmin.DELETE_MANY) {
            const encodedIdResources = [
                'troubleshooting',
                'appliance-info',
                'assistance',
            ];
            return Promise.all(
                params.ids.map((id) =>
                    httpClient(
                        `${apiUrl}/${resource}/${
                            encodedIdResources.includes(resource)
                                ? id
                                : encodeURIComponent(id)
                        }`,
                        {
                            method: 'DELETE',
                        }
                    )
                )
            ).then((responses) => ({
                data: responses.map((response) => response.json),
            }));
        }
        const _a = convertDataRequestToHTTP(type, resource, params);
        const { url } = _a;
        const { options } = _a;
        return httpClient(url, options).then((response) =>
            convertHTTPResponse(response, type, resource, params)
        );
    };
    return provider;
};
